import {
  DollarOutlined,
  CalendarOutlined,
  UsergroupAddOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import {Space, Button, Tooltip} from "antd";
import {useSelector} from "react-redux";
import type {
  CustomerLiveEventBooking,
  LegacyCustomerEvent,
} from "../../../../../features/customer-live-events";
import {useJoinCustomerLiveEvent} from "../../../../../features/customer-live-events";
import {
  buildGoogleMapsDirectionsUrl,
  launchMeeting,
} from "../../../../../features/live-events";
import {RootState} from "../../../../../redux/store";
import {StyledCard, StyledClassCardContent} from "../card.style";

export type TCustomerLiveClassCardProps = {
  eventData: LegacyCustomerEvent | CustomerLiveEventBooking;
};

function isLegacyEvent(
  event: LegacyCustomerEvent | CustomerLiveEventBooking
): event is LegacyCustomerEvent {
  return "event_date" in event;
}

export function CustomerLiveClassCard({eventData}: TCustomerLiveClassCardProps) {
  const {userInfo} = useSelector((state: RootState) => state.userLogin);
  const joinMutation = useJoinCustomerLiveEvent();

  // Normalize fields across legacy and new event types
  const isLegacy = isLegacyEvent(eventData);
  const eventDate = isLegacy ? eventData.event_date : eventData.eventDate;
  const customerEventTime = isLegacy
    ? eventData.customer_event_time
    : eventData.customerEventTime;
  const coverImage = isLegacy
    ? eventData.liveEvent.cover_image ||
      (eventData.liveEvent.cover_images?.[0]?.media_url ?? "")
    : eventData.liveEvent.coverImage;
  const description = isLegacy
    ? eventData.liveEvent.description
    : eventData.liveEvent.description ?? "";
  const callPrice = isLegacy
    ? eventData.liveEvent.callPrice
    : eventData.liveEvent.callPrice ?? eventData.amount;
  const callPriceCurrency = isLegacy
    ? eventData.liveEvent.callPriceCurrency
    : eventData.liveEvent.callPriceCurrency ?? eventData.currency;
  const attendees = isLegacy
    ? eventData.liveEvent.attendees
    : eventData.liveEvent.attendees;
  // Physical location is v2-only; legacy events have no venue concept.
  const videoPlatform = isLegacy
    ? eventData.liveEvent.video_platform ?? ""
    : eventData.liveEvent.videoPlatform;
  const locationAddress = isLegacy ? undefined : eventData.liveEvent.locationAddress;
  const locationLat = isLegacy ? undefined : eventData.liveEvent.locationLat;
  const locationLng = isLegacy ? undefined : eventData.liveEvent.locationLng;
  const isPhysicalLocation = videoPlatform?.toLowerCase() === "physicallocation";

  function handleGetDirections() {
    if (locationLat == null || locationLng == null) return;
    window.open(buildGoogleMapsDirectionsUrl(locationLat, locationLng), "_blank");
  }

  function handleJoinLiveClass() {
    const {eventId, reference} = eventData;

    joinMutation.mutate(
      {eventId, reference},
      {
        onSuccess: (result) => {
          const {url, target} = launchMeeting({
            joinResult: result,
            user: {
              firstName: userInfo?.user?.firstName ?? "",
              lastName: userInfo?.user?.lastName ?? "",
            },
            event: {name: eventData.liveEvent.name},
            role: "guest",
          });
          window.open(url, target);
        },
      }
    );
  }

  const eventDescription = description.replace(/<[^>]+>/g, "");

  const renderPrice = () => {
    if (callPrice === 0) return "Free";
    if (callPrice === undefined) return "—";
    return `${callPriceCurrency} ${callPrice.toLocaleString("en-US")}`;
  };

  const renderAttendees = () => {
    if (attendees === undefined) return null;
    return (
      <div>
        <Space size={10}>
          <UsergroupAddOutlined style={{color: "#222222"}} />
          <span>{attendees === -1 ? "Unlimited" : attendees} attending</span>
        </Space>
      </div>
    );
  };

  const renderLocation = () => {
    if (!isPhysicalLocation || !locationAddress) return null;
    return (
      <div>
        <Space size={10}>
          <EnvironmentOutlined style={{color: "#222222"}} />
          <span>{locationAddress}</span>
        </Space>
      </div>
    );
  };

  return (
    <StyledCard title={eventData.liveEvent.name}>
      <StyledClassCardContent>
        <div className="class-card-image-wrapper">
          <img
            className="class-card-image"
            src={coverImage || "/images/no-image-placeholder.png"}
            alt={eventData.liveEvent.name}
          />
        </div>
        {eventDescription && <p className="class-card-info-text">{eventDescription}</p>}
        <div className="class-card-extra-info">
          <div>
            <Space size={10}>
              <DollarOutlined style={{color: "#222222"}} />
              <span>{renderPrice()}</span>
            </Space>
          </div>
          <div>
            <Space size={10}>
              <CalendarOutlined style={{color: "#222222"}} />
              <span>
                {new Date(eventDate).toDateString()}, {customerEventTime}
              </span>
            </Space>
          </div>
          {renderAttendees()}
          {renderLocation()}
        </div>
        <div className="class-card-start-btn">
          <Tooltip title={isPhysicalLocation ? "Get directions" : "Join event"}>
            <Button
              onClick={isPhysicalLocation ? handleGetDirections : handleJoinLiveClass}
              type="primary"
              block
              size="small"
              disabled={isPhysicalLocation && locationLat == null}
              loading={!isPhysicalLocation && joinMutation.isLoading}
            >
              {isPhysicalLocation ? "Get Directions" : "Join event"}
            </Button>
          </Tooltip>
        </div>
      </StyledClassCardContent>
    </StyledCard>
  );
}
